import { formatDate } from 'i18n/format';
import { t as translate } from 'i18n';
/**
 * Pure helpers behind the answer widgets: the answer's widget list, value
 * formatting and the chart.js data for line / bar / stacked / pie charts.
 * Kept free of chart.js imports so they test without a canvas.
 *
 * A builder that cannot draw its chart faithfully returns `{fallback: code}`
 * instead of data: the card shows the table with that code's note.
 */
import { formatAmount, formatNumber, unitOf } from './units';

/** The console shows at most this many rows of a table. */
export const TABLE_MAX_ROWS = 500;

/** Circular legends and funnel stage lists stay bounded; more rows use the table. */
export const PIE_MAX_SLICES = 30;

export const WIDGET_WIDTHS = ['full', 'half', 'quarter'];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * An answer's widgets in the model's order, `[{index, width, widget}]`;
 * `index` is the widget's place in the list (its data endpoint).
 */
export function answerWidgets(content) {
  if (!Array.isArray(content?.widgets)) return [];
  return content.widgets
    .map((widget, index) =>
      isObject(widget)
        ? { index, width: WIDGET_WIDTHS.includes(widget.width) ? widget.width : 'full', widget }
        : null,
    )
    .filter(Boolean);
}

/** Locale-aware display only; calendar dates keep their original day and query values. */
export function formatValue(value, column) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? translate('assistant.diagnosticResultsYes') : translate('assistant.diagnosticResultsNo');
  if (typeof value === 'number') return formatNumber(value, column);
  if (column?.type === 'time') {
    const m = ISO_DATE.exec(String(value));
    if (m) {
      const day = formatDate(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
      // Hourly buckets of one day are different rows, not one date repeated.
      const hour = column.hourly && /^.{10}[T ](\d{2})/.exec(String(value));
      return hour ? `${day} ${hour[1]}:00` : day;
    }
  }
  return String(value);
}

/**
 * Rows keyed by column key. The endpoint returns objects; positional arrays
 * (in `columns` order) are accepted too. Anything else is dropped.
 */
export function normalizeRows(rows, columns) {
  if (!Array.isArray(rows)) return [];
  const keys = (columns || []).map((c) => c.key);
  return rows
    .map((row) => {
      if (Array.isArray(row)) return Object.fromEntries(keys.map((k, i) => [k, row[i]]));
      if (row && typeof row === 'object') return row;
      return null;
    })
    .filter(Boolean);
}

const toNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * chart.js `data` for a line / bar / stacked chart.
 *
 * Without `series`: one dataset per `y` key over the `x` values. With
 * `series`: one dataset per distinct series value (per `y` key when there are
 * several), aligned to the distinct `x` values; gaps are null.
 *
 * Every grouping column besides `x` must be the series (a lone dimension is
 * taken as the series when none is named): otherwise rows would overwrite
 * each other on one x — `{fallback: 'shape'}`, as for unknown keys.
 *
 * Units: the `y` measures' units in order of first use are the `axes` — the
 * first on the left (`y`), a second on the right (`y1`); each dataset names
 * its `axis` and `unit`. More than two units, or a stacked chart of more than
 * one, cannot be drawn honestly: `{fallback: 'units'}`. ECharts may opt into
 * three independent axes with `maxUnits: 3`; the legacy default stays two.
 * Repeated (formatted x, series) cells return `shape` instead of overwriting.
 *
 * @param {{kind: string, x: string, y: string[], series?: string|null}} chart
 * @param {Array<{key: string, title: string, type: string, format?: string}>} columns
 * @param {Array<object>} rows  normalised rows
 * @param {string[]} palette    colours, cycled
 */
export function buildChartData(chart, columns, rows, palette, { maxUnits = 2 } = {}) {
  const byKey = Object.fromEntries((columns || []).map((c) => [c.key, c]));
  const xCol = byKey[chart?.x];
  const yKeys = (chart?.y || []).filter((k) => byKey[k]);
  if (!xCol || yKeys.length === 0) return { fallback: 'shape' };
  const rest = (columns || []).filter((c) => c.kind !== 'measure' && c.key !== xCol.key);
  const seriesKey = chart.series || (rest.length === 1 ? rest[0].key : null);
  if (rest.some((c) => c.key !== seriesKey)) return { fallback: 'shape' };

  const units = [];
  for (const key of yKeys) {
    const unit = unitOf(byKey[key]);
    if (!units.includes(unit)) units.push(unit);
  }
  if (units.length > maxUnits || (chart.kind === 'stacked' && units.length > 1)) return { fallback: 'units' };
  const axes = units.map((unit, i) => ({
    id: i === 0 ? 'y' : `y${i}`,
    unit,
    titles: yKeys.filter((k) => unitOf(byKey[k]) === unit).map((k) => byKey[k].title || k),
  }));
  const axisOf = (key) => axes[units.indexOf(unitOf(byKey[key]))].id;

  const labelOf = (row) => formatValue(row[xCol.key], xCol);
  const labels = [];
  const labelIndex = new Map();
  const labelValues = new Map();
  for (const row of rows) {
    const label = labelOf(row);
    // Equal display labels must not place different raw coordinates on one x.
    if (labelValues.has(label) && !Object.is(labelValues.get(label), row[xCol.key])) return { fallback: 'shape' };
    labelValues.set(label, row[xCol.key]);
    if (!labelIndex.has(label)) {
      labelIndex.set(label, labels.length);
      labels.push(label);
    }
  }

  const colour = (i) => palette[i % palette.length];
  const seriesCol = seriesKey ? byKey[seriesKey] : null;
  const datasets = [];
  const dataset = (label, key, groupRows, i) => {
    const data = new Array(labels.length).fill(null);
    const seen = new Set();
    for (const row of groupRows) {
      const index = labelIndex.get(labelOf(row));
      // Distinct rows must never silently replace one another in a chart.
      if (seen.has(index)) return null;
      seen.add(index);
      data[index] = toNumber(row[key]);
    }
    return { label, data, colour: colour(i), axis: axisOf(key), unit: unitOf(byKey[key]) };
  };

  if (!seriesCol) {
    yKeys.forEach((key, i) => datasets.push(dataset(byKey[key].title || key, key, rows, i)));
  } else {
    const groups = new Map();
    const labelCounts = new Map();
    for (const row of rows) {
      const identity = row[seriesCol.key];
      if (groups.has(identity)) { groups.get(identity).rows.push(row); continue; }
      const name = formatValue(row[seriesCol.key], seriesCol);
      groups.set(identity, { name, rows: [row] });
      labelCounts.set(name, (labelCounts.get(name) || 0) + 1);
    }
    const names = new Set();
    let i = 0;
    for (const [identity, group] of groups) {
      const name = labelCounts.get(group.name) > 1 ? `${group.name} (${JSON.stringify(identity) ?? 'undefined'})` : group.name;
      if (names.has(name)) return { fallback: 'shape' };
      names.add(name);
      for (const key of yKeys) {
        const label = yKeys.length > 1 ? `${name} · ${byKey[key].title || key}` : name;
        datasets.push(dataset(label, key, group.rows, i));
        i += 1;
      }
    }
  }
  if (datasets.some((item) => item === null)) return { fallback: 'shape' };
  return { labels, datasets, axes };
}

// Slices past the palette: the same hues again, each round paler.
const PIE_STRENGTHS = ['', 'cc', '99', '66', '40'];

/**
 * A pie: one slice per row — the `x` value and its one `y` measure — with each
 * slice's share of the total (0–100). `{fallback: 'pie'}` for anything a pie
 * cannot draw: not exactly one measure, another grouping column, no rows or
 * more than {@link PIE_MAX_SLICES}, a missing or negative value, a zero total.
 * Circular charts and funnels keep this cap to bound their legend/stage lists.
 * Treemaps can override it because their labels are drawn inside the canvas.
 */
export function buildPieData(chart, columns, rows, palette, { maxSlices = PIE_MAX_SLICES } = {}) {
  const byKey = Object.fromEntries((columns || []).map((c) => [c.key, c]));
  const xCol = byKey[chart?.x];
  const yKeys = (chart?.y || []).filter((k) => byKey[k]);
  const refused = { fallback: 'pie' };
  if (!xCol || yKeys.length !== 1 || (chart?.y || []).length !== 1) return refused;
  if ((columns || []).some((c) => c.kind !== 'measure' && c.key !== xCol.key)) return refused;
  if (rows.length === 0 || rows.length > maxSlices) return refused;
  const yCol = byKey[yKeys[0]];
  const values = rows.map((row) => toNumber(row[yCol.key]));
  if (values.some((v) => v === null || v < 0)) return refused;
  const total = values.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return refused;

  const colour = (i) => {
    const round = Math.floor(i / palette.length);
    return `${palette[i % palette.length]}${PIE_STRENGTHS[round % PIE_STRENGTHS.length]}`;
  };
  return {
    title: yCol.title || yCol.key,
    unit: unitOf(yCol),
    total,
    slices: rows.map((row, i) => ({
      label: formatValue(row[xCol.key], xCol),
      value: values[i],
      share: (values[i] / total) * 100,
      colour: colour(i),
    })),
  };
}

/**
 * KPI tiles: each `y` measure's value in the single row, roubles whole;
 * `{fallback: 'kpi'}` when the result has several rows (one of them is not
 * the total).
 */
export function kpiValues(chart, columns, rows) {
  if (rows.length > 1) return { fallback: 'kpi' };
  const byKey = Object.fromEntries((columns || []).map((c) => [c.key, c]));
  const first = rows[0] || {};
  const show = (value, column) => (typeof value === 'number' ? formatAmount(value, column) : formatValue(value, column));
  return (chart?.y || [])
    .filter((k) => byKey[k])
    .map((key) => ({ key, title: byKey[key].title || key, value: show(first[key], byKey[key]) }));
}
